import Vue from 'vue'
import store from '@/store'
import ReconnectingWebSocket from 'reconnecting-websocket';
import { Action } from '@/utilities/websocket'

function generateRandom(min, max, step) {
  const randomNum = min + Math.random() * (max - min);
  return Math.round(randomNum / step) * step;
}

export function Websocket(path, base = null, handlers = {}) {
  const baseURL = base || process.env.VUE_APP_WSS || 'wss://localhost:3000/ws/'
  let url = new URL(path, baseURL)
  url = new URL(url.href.replace("localhost", "127.0.0.1"))

  let isAuthenticated = false
  let authCheckPending = false
  let authPending = false
  let reauthRetryCount = 0
  let authRetryCount = 0
  const MAX_AUTH_RETRIES = 5
  const MAX_REAUTH_RETRIES = 5
  
  this.connection = new ReconnectingWebSocket(url.href, [], {
    connectionTimeout: generateRandom(5000, 15000, 50),
    reconnectionDelayGrowFactor: 2,
    maxReconnectionDelay: 30000, // cap backoff at 30s
    maxRetries: Infinity,        // keep retrying so realtime survives outages > a few minutes (backoff is capped at 30s above)
    minReconnectionDelay: 1000,  // at least 1s between retries
  })

  const checkAuth = () => {
    if (!this.isOpen() || authCheckPending) return
    authCheckPending = true
    this.connection.send(JSON.stringify({ action: Action.CHECK_AUTH }))
  }

  const authenticate = (token, realm) => {
    if (!this.isOpen() || authPending) return false
    if (!token || !realm) {
      console.warn('Cannot authenticate: Token or realm is missing')
      return false
    }
    authPending = true
    this.connection.send(JSON.stringify({
      action: Action.AUTHENTICATE,
      token: token,
      realm: realm
    }))
    return true
  }

  const reauthenticate = (token, realm) => {
    if (!this.isOpen()) {
      console.warn('Cannot re-authenticate: WebSocket is not open')
      return false
    }
    if (!token || !realm) {
      console.warn('Cannot re-authenticate: Token or realm is missing')
      return false
    }
    this.connection.send(JSON.stringify({
      action: Action.REAUTHENTICATE,
      token: token,
      realm: realm
    }))
    return true
  }

  // Expose authentication methods
  this.checkAuth = checkAuth
  this.authenticate = authenticate
  this.reauthenticate = reauthenticate
  this.isAuthenticated = () => isAuthenticated

  this.sendMessage = (action, data) => {
    const context = {
      action: action,
      data: data
    }
    return this.connection.send(JSON.stringify(context))
  }

  this.onMessage = () => {}

  this.isOpen = () => {
    return this.connection.readyState === WebSocket.OPEN;
  }

  /**
   * Close the connection only if it is already open. Avoids "WebSocket is closed
   * before the connection is established" when the user navigates away while
   * the socket is still connecting (CONNECTING state).
   */
  this.close = () => {
    if (this.connection.readyState === WebSocket.OPEN) {
      this.connection.close();
    }
  }

  const handleAuthError = (result) => {
    authPending = false
    authCheckPending = false
    reauthRetryCount = 0
    authRetryCount = 0
    Vue.prototype.$flashStorage.flash(result.data.error, 'error', {'timeout': 8000})
    setTimeout(() => {
      window.location.reload()
    }, 8000)
  }

  const handleCheckAuth = (result, token, realm) => {
    isAuthenticated = result.is_authenticated === true
    authCheckPending = false
    if (!isAuthenticated) {
      authenticate(token, realm)
    } else if (handlers.onAuthSuccess) {
      handlers.onAuthSuccess()
    }
  }

  const handleAuthenticate = (result, token, realm) => {
    isAuthenticated = result.success === true
    authPending = false
    if (isAuthenticated) {
      authRetryCount = 0
      if (handlers.onAuthSuccess) {
        handlers.onAuthSuccess()
      }
      return
    }
    
    authRetryCount++
    if (authRetryCount < MAX_AUTH_RETRIES) {
      authenticate(token, realm)
    } else {
      console.error('Failed to authenticate after maximum retries')
    }
  }

  const handleReauthenticate = (result, token, realm) => {
    isAuthenticated = result.success === true
    authPending = false

    if (isAuthenticated) {
      reauthRetryCount = 0
      if (handlers.onAuthSuccess) {
        handlers.onAuthSuccess()
      }
      return
    }

    reauthRetryCount++
    if (reauthRetryCount < MAX_REAUTH_RETRIES) {
      if (!reauthenticate(token, realm)) {
        reauthRetryCount--
      }
    } else {
      console.error('Failed to re-authenticate after maximum retries')
    }
  }

  const handleMessage = (event, result) => {
    const auth = store?.state?.security?.auth
    const token = auth?.token
    const realm = auth?.realm

    switch (result.action) {
      case Action.AUTH_ERROR:
        handleAuthError(result)
        return
      case Action.CHECK_AUTH:
        handleCheckAuth(result, token, realm)
        return
      case Action.AUTHENTICATE:
        handleAuthenticate(result, token, realm)
        return
      case Action.REAUTHENTICATE:
        handleReauthenticate(result, token, realm)
        return
      default:
        this.onMessage(event, result)
    }
  }

  this.connection.onmessage = (event) => {
    const raw = event.data
    if (raw === undefined || raw === null || String(raw).trim() === '') {
      console.warn('Invalid message received:', raw)
      return
    }
    let result = null
    try {
      result = JSON.parse(raw)
    } catch (error) {
      console.error('Error parsing JSON:', error)
      return
    }
    if (result === null || typeof result !== 'object') {
      return
    }
    handleMessage(event, result)
  }

  this.connection.addEventListener('open', (event) => {
    // Re-register this instance: the 'close' handler removes it from the store
    // on every transient drop, but ReconnectingWebSocket reuses this wrapper
    // across reconnects. Without re-adding on open, the live socket would be
    // missing from websocketInstances after the first reconnect, so
    // notifyWebSocketTokenRefresh() could no longer reauthenticate it. The
    // registry is a Set, so re-adding on the initial open is a harmless no-op.
    store.commit('addWebSocketInstance', this)
    // Reset retry counts when connection opens
    reauthRetryCount = 0
    authRetryCount = 0
    
    // Check authentication status when connection opens
    // Authentication will happen based on the checkAuth response
    const auth = store.state.security.auth
    if (auth?.token && auth?.realm) {
      checkAuth()
    }
    
    if (handlers.onopen) {
      handlers.onopen(event)
    }
  })

  this.connection.addEventListener('close', (event) => {
    isAuthenticated = false
    authCheckPending = false
    authPending = false
    // Reset retry counts when connection closes
    reauthRetryCount = 0
    authRetryCount = 0
    // Remove from registry when closed
    store.commit('removeWebSocketInstance', this)
    if (handlers.onclose) {
      handlers.onclose(event)
    }
  })

  this.connection.addEventListener('error', (event) => {
    const message = event?.message
    if (message) {
      Vue.prototype.$flashStorage.flash(message, 'error', {'timeout': 8000})
    }
  })

  store.commit('addWebSocketInstance', this)

  return this;
}

export function notifyWebSocketTokenRefresh(token, realm) {
  const websocketInstances = store.state.websocket.websocketInstances
  websocketInstances.forEach(instance => {
    // Guard each instance: a stale/closed entry must not throw here, or it
    // breaks token propagation (this runs inside authLogin on every refresh).
    try {
      if (instance && typeof instance.reauthenticate === 'function') {
        instance.reauthenticate(token, realm)
      }
    } catch (e) {
      console.error('Failed to reauthenticate websocket instance', e)
    }
  })
}
